// What a finished run did, as numbers.
//
// **Read off the final state, never replayed.** `scorecard.ts` replays a log
// and is the right tool for a game submitted with consent, where the log *is*
// the artifact. It cannot answer this one: a run saves as a snapshot, so a run
// continued in a fresh tab has an empty log and a scorecard built from it would
// report a game that lasted four moves. Everything below is on the board, in
// the run, or in the tally the reducer keeps precisely because the board cannot
// say it afterwards.
//
// **Counts, never content.** Every field is a number or a word from a list this
// repo owns. There is no log here, no position, no move — the difference
// between this and the consented submission is that this cannot be replayed,
// and that difference is what the consent screen and the banner both describe.
//
// **Twenty-four fields, and the ceiling is real**: GA4 takes twenty-five custom
// parameters on an event and silently drops the rest, so "as many as will fit"
// is a number rather than a feeling — the first draft of this was twenty-seven
// and the test below is what said so. One spare, deliberately, because the next
// thing anybody wants is always one more field.

import { pieceColor } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { pieceAt } from "src/engine/rules/position";
import { beltDirAt, bufferTotal, heapAt, NO_BELT, structureSquares, unitAt, unitGrade } from "src/engine/rules/industry/state";
import { EMPTY_TALLY } from "src/engine/rules/tally";
import { hasPower, POWERS } from "src/engine/rules/campaign/powers";
import { stagesCleared } from "src/engine/ai/waves";

/** What the app knows and the board does not. */
export interface RunContext {
  /** Won or lost — the shell's own reading, so one answer serves both. */
  outcome: "won" | "lost";
  /** How hard it was asked to get. */
  difficulty?: string;
  /** Total time in the run, in ms. See `game/runSave.ts`. */
  played?: number;
}

const round = (n: number): number => Math.round(n);

/**
 * Everything a finished run is worth counting.
 *
 * Flat, because an event parameter cannot be an object; and shallow, because a
 * field somebody has to compute in the query is a field that will be computed
 * two different ways.
 */
export function runReport(state: GameState, context: RunContext): Record<string, string | number> {
  const run = state.run;
  const side = run?.side ?? 0;
  const tally = state.tally ?? EMPTY_TALLY;
  const total = state.runTally ?? EMPTY_TALLY;

  // The board, counted once. One sweep for all of it — `cellList` is the
  // board's own order and this runs exactly once per run.
  let mine = 0;
  let theirs = 0;
  let belts = 0;
  let scrap = 0;
  let material = 0;
  let best = 0;
  for (const square of state.position.cellList) {
    if (beltDirAt(state.industry, square) !== NO_BELT) belts++;
    scrap += heapAt(state.industry, square);
    const piece = pieceAt(state.position, square);
    if (piece) {
      if (pieceColor(piece) === side) {
        mine++;
      } else {
        theirs++;
      }
      // Held material, ours only: what the *player's* factory was sitting on
      // when it ended is a fact about their play, and an enemy's hopper is not.
      if (pieceColor(piece) === side) {
        for (let i = 0; i < bufferTotal(state.industry, square, "in"); i++) {
          const unit = unitAt(state.industry, square, i, "in");
          material++;
          if (unitGrade(unit) > best) best = unitGrade(unit);
        }
      }
    }
  }

  const objectives = run?.objectives ?? [];

  return {
    outcome: context.outcome,
    status: state.status,
    difficulty: context.difficulty ?? "unknown",
    // The level, which is the one string here that is content — and it is
    // *ours*: a level name ships in the repo, so it names a map rather than
    // anything a player wrote.
    level: run?.level?.levelName ?? "unknown",
    seed: state.seed ?? 0,

    // How long, three ways, because they answer different questions: fronts is
    // progress, plies is the size of the game and minutes is whether anybody
    // has that long.
    fronts: stagesCleared(state),
    plies: state.history.length,
    minutes: round((context.played ?? 0) / 60000),

    // The scorecard the game already keeps. `runTally` is every closed front
    // and `tally` is the one it ended on, which is why both are added — the
    // ending reads `runTally + tally` everywhere else too.
    moves: total.moves + tally.moves,
    taken: total.taken + tally.taken,
    score: (total.score ?? 0) + (tally.score ?? 0),
    mined: total.mined + tally.mined,

    // What the run had reached.
    objectives_done: objectives.filter((entry) => entry.done).length,
    objectives_total: objectives.length,
    cards_played: run?.spent.length ?? 0,
    // Powers are structures standing now (§26 F6), so the count is read off
    // the board — which is also the honest number for a report about the end.
    powers: run
      ? POWERS.filter((id) => hasPower(state.industry, run.side, id)).length
      : 0,

    // And what the board looked like at the end: the shape of the factory that
    // got them there, or did not.
    belts,
    structures: structureSquares(state.industry).length,
    pieces: mine,
    enemies: theirs,
    material,
    top_grade: best,
    scrap_left: scrap,
    cells: state.position.cellList.length
  };
}
